import { Box, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

import type { SalesOrgLeadDetail } from "@xop/dashboard-shared";

import { toneChipSx, tonePalette } from "../heatStyles.js";
import {
  passSummary,
  qualityChecks,
  scoreBand,
  type QualityCheck,
  type ScoreBand,
  type Tone,
} from "../quality.js";
import { panelInsetSx, panelSx } from "./primitives.js";

/* ── 1b. Quality panel — the "why it's confirmed" centerpiece ───── */

export function QualityPanel({ lead }: { lead: SalesOrgLeadDetail }): JSX.Element {
  const checks = qualityChecks(lead);
  const { passed, total } = passSummary(checks);
  const scored = lead.eval_verdict != null || lead.eval_at != null;
  const hasScores = lead.eval_grounding != null || lead.eval_fit != null || lead.eval_need != null;
  return (
    <Box component="section" sx={[panelSx, { p: 3, gap: 2 }]}>
      <Box component="header" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography component="h3" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 14, fontWeight: 700, color: "text.primary" }}>
          <Box component="span" aria-hidden>🛡️</Box> Why this lead qualifies
        </Typography>
        <Box
          sx={[
            { display: "inline-flex", alignItems: "center", borderRadius: 999, border: 1, px: 1, py: 0.25, fontSize: 12, fontWeight: 600 },
            toneChipSx(passed === total ? "good" : passed >= total - 1 ? "warn" : "bad"),
          ]}
        >
          {passed}/{total} checks passed
        </Box>
      </Box>

      {scored && hasScores ? (
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { sm: "repeat(3, minmax(0, 1fr))" } }}>
          <ScoreMeter label="Evidence grounding" hint="Is the premise publicly provable?" score={lead.eval_grounding} />
          <ScoreMeter label="Product fit" hint="Match to an XOP product / vertical?" score={lead.eval_fit} />
          <ScoreMeter
            label="Current need"
            hint={lead.lead_kind === "fit" ? "An evidenced operational fit?" : "A dated buying trigger right now?"}
            score={lead.eval_need}
          />
        </Box>
      ) : null}

      {lead.eval_reason ? (
        <Box sx={[panelInsetSx, { p: 1.75, display: "grid", gap: 0.625 }]}>
          <Box sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
            Independent quality review
          </Box>
          <Typography sx={{ fontSize: 14, lineHeight: 1.65, color: "text.secondary", fontStyle: "italic" }}>
            {"“"}{lead.eval_reason}{"”"}
          </Typography>
        </Box>
      ) : null}

      <QualityChecklist checks={checks} />

      {!scored ? (
        <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
          The independent quality judge hasn{"’"}t scored this lead yet — the
          checks above reflect the evidence currently on file.
        </Typography>
      ) : null}
    </Box>
  );
}

function ScoreMeter({ label, hint, score }: { label: string; hint: string; score: number | null | undefined }): JSX.Element {
  const band: ScoreBand = scoreBand(score);
  const pct = score == null ? 0 : Math.max(0, Math.min(100, Math.round(score * 100)));
  return (
    <Box sx={[panelInsetSx, { p: 1.5, display: "grid", gap: 0.875 }]}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{label}</Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Box sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1, color: (t: Theme) => tonePalette(t, band.tone) }}>{pct}</Box>
          <Box sx={{ fontSize: 11, color: "text.disabled" }}>/100 · {band.label}</Box>
        </Box>
      </Box>
      <Box
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        sx={{ height: 8, borderRadius: 999, bgcolor: (t: Theme) => alpha(t.palette.text.primary, 0.08), overflow: "hidden" }}
      >
        <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: 999, bgcolor: (t: Theme) => tonePalette(t, band.tone), transition: "width 450ms ease" }} />
      </Box>
      <Box sx={{ fontSize: 11, color: "text.disabled" }}>{hint}</Box>
    </Box>
  );
}

function QualityChecklist({ checks }: { checks: QualityCheck[] }): JSX.Element {
  return (
    <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
      {checks.map((c) => (
        <Box component="li" key={c.key} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <CheckDot state={c.state} />
          <Box sx={{ display: "grid", gap: 0.125, minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>{c.label}</Box>
            <Box sx={{ fontSize: 12, lineHeight: 1.5, color: "text.secondary", overflowWrap: "anywhere" }}>{c.detail}</Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function CheckDot({ state }: { state: QualityCheck["state"] }): JSX.Element {
  const tone: Tone = state === "pass" ? "good" : state === "warn" ? "warn" : "bad";
  const icon = state === "pass" ? "✓" : state === "warn" ? "!" : "✕";
  return (
    <Box
      aria-hidden
      sx={{ flexShrink: 0, mt: 0.125, display: "grid", placeItems: "center", height: 20, width: 20, borderRadius: "50%", fontSize: 12, fontWeight: 700, color: (t: Theme) => tonePalette(t, tone), bgcolor: (t: Theme) => alpha(tonePalette(t, tone), 0.15) }}
    >
      {icon}
    </Box>
  );
}
